import { LoadingButton } from '@mui/lab';
import { Button, Card, CardHeader, Divider, Skeleton, Stack, Switch } from '@mui/material';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router';

import { CoinInfo } from '@/components/wallets';
import { useGetMachine } from '@/store/api/services/machines/machine';

import {
  useGetWalletsConfigQuery,
  usePutAtmCoinsMutation,
} from '@/store/api/services/wallets/wallets';

import { sleep } from '@/helpers/sleep';
import type { WalletCoin } from '@/types';

const Loading = () =>
  Array.from({ length: 3 }, (_, idx) => <Skeleton variant="rounded" key={idx} height={44} />);

const MachineCoinsSettings: React.FC = () => {
  const { id = '0' } = useParams<{ id: string }>();
  const atmId = Number(id);

  const { data: configResponse, isLoading: isConfigLoading } = useGetWalletsConfigQuery();
  const { data: machineResponse, isLoading: isMachineLoading, refetch } = useGetMachine(atmId);
  const [putAtmCoins, { isLoading: isSaving }] = usePutAtmCoinsMutation();

  const [initialCoinState, setInitialCoinState] = useState<Record<string, boolean | null>>({});
  const [coinState, setCoinState] = useState<Record<string, boolean | null>>({});

  const isChanged = JSON.stringify(coinState) !== JSON.stringify(initialCoinState);
  const isLoading = isConfigLoading || isMachineLoading;

  useEffect(() => {
    if (configResponse && machineResponse && Object.keys(initialCoinState).length === 0) {
      const allCoins: WalletCoin[] = configResponse.data[0]?.coins || [];
      const enabledIds: string[] = machineResponse.data.enabled_coin_ids || [];
      const state: Record<string, boolean | null> = {};
      allCoins.forEach((coin) => {
        if (!coin.is_enabled || coin.ticker === 'BTC') {
          state[coin.id] = null;
        } else {
          state[coin.id] = enabledIds.includes(coin.id);
        }
      });
      setCoinState(state);
      setInitialCoinState(state);
    }
  }, [isLoading, configResponse, machineResponse, initialCoinState]);

  const handleReset = () => setCoinState(initialCoinState);
  const handleToggle = (coinId: string) =>
    setCoinState((prev) => ({ ...prev, [coinId]: !prev[coinId] }));
  const handleSave = async () => {
    try {
      const availableIds = Object.keys(coinState).filter((id) => coinState[id] !== null);
      const enable = availableIds.filter((id) => coinState[id]);
      const disable = availableIds.filter((id) => !coinState[id]);
      await putAtmCoins({ atm_ids: [atmId], enable, disable }).unwrap();
      toast.success('Settings successfully saved');
      setInitialCoinState(coinState);
      sleep(1000);
      refetch();
    } catch (error) {
      const e = error as any;
      const message = e?.data?.error?.name;
      if (message) toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader title="Coin Settings" sx={{ p: 2 }} />
      <Divider />
      <Stack p={2} gap={2}>
        {isLoading && <Loading />}
        {!isLoading &&
          (configResponse?.data[0]?.coins || []).map((coin) => (
            <Stack
              key={coin.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ opacity: !(coin.ticker === 'BTC' || coin.is_enabled) ? 0.4 : 1 }}
            >
              <CoinInfo coin={coin} />
              <Switch
                checked={(coin.ticker === 'BTC' || coinState[coin.id]) ?? false}
                onChange={() => handleToggle(coin.id)}
                disabled={!coin.is_enabled || coin.ticker === 'BTC' || isSaving || isLoading}
              />
            </Stack>
          ))}
      </Stack>
      <Divider />
      <Stack direction="row" gap={1} justifyContent="flex-end" p={2}>
        {isChanged && <Button onClick={handleReset}>Reset</Button>}
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          disabled={!isChanged || isSaving || isLoading}
          loading={isSaving}
        >
          Save Settings
        </LoadingButton>
      </Stack>
    </Card>
  );
};

export default MachineCoinsSettings;
